/**
 * Graph traversal utilities for flowchart nodes
 * 
 * Functions for finding connected nodes, building adjacency lists,
 * and traversing dependency graphs.
 */

import { getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Get all dependency task IDs for a task
 * Uses unlockConditions if present (v1 or v2), otherwise falls back to dependsOn
 */
export function getEffectiveDependencyIds(task, getDepId, indexParentTasks = null) {
  const ids = getAllConditionTaskIds(task.unlockConditions, indexParentTasks);
  if (ids.length > 0) return ids;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map(getDepId).filter(Boolean);
}

/**
 * Find all nodes connected to the given seed nodes
 * Traverses both parent and child relationships to find the entire connected subgraph
 * 
 * @param {Set} seedNodeIds - Initial selected node IDs
 * @param {Array} tasks - All tasks
 * @returns {Set} All connected node IDs (including seeds)
 */
export function findConnectedNodes(seedNodeIds, tasks) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const getDepId = (dep) => typeof dep === 'string' ? dep : dep?.taskId;
  
  // Build adjacency lists (both directions)
  const children = new Map(); // parent -> [children]
  const parents = new Map();  // child -> [parents]
  
  tasks.forEach(task => {
    if (!children.has(task.id)) children.set(task.id, []);
    if (!parents.has(task.id)) parents.set(task.id, []);
    
    const parentIds = getEffectiveDependencyIds(task, getDepId, tasks);
    parentIds.forEach(parentId => {
      if (taskMap.has(parentId)) {
        parents.get(task.id).push(parentId);
        if (!children.has(parentId)) children.set(parentId, []);
        children.get(parentId).push(task.id);
      }
    });
  });
  
  // BFS to find all connected nodes
  const connected = new Set();
  const queue = [...seedNodeIds];
  
  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (connected.has(nodeId)) continue;
    if (!taskMap.has(nodeId)) continue;
    
    connected.add(nodeId);
    
    // Add all parents
    const nodeParents = parents.get(nodeId) || [];
    nodeParents.forEach(pid => {
      if (!connected.has(pid)) queue.push(pid);
    });
    
    // Add all children
    const nodeChildren = children.get(nodeId) || [];
    nodeChildren.forEach(cid => {
      if (!connected.has(cid)) queue.push(cid);
    });
  }
  
  return connected;
}
